import { useEffect, useState } from 'react';

/**
 * Tracks seconds remaining until `until`. Returns `{ secondsLeft, isActive }`.
 *
 * - Pass `null` (or a past date) to indicate no active cooldown.
 * - Re-pass a new `Date` to (re)start the countdown — the hook keys off the
 *   timestamp value, so calling with the same Date instance won't restart.
 * - Cleans up its interval on unmount and when the cooldown elapses.
 */
export function useCooldown(until: Date | null): {
  secondsLeft: number;
  isActive: boolean;
} {
  const computeSecondsLeft = (target: Date | null): number => {
    if (!target) return 0;
    const diffMs = target.getTime() - Date.now();
    return diffMs > 0 ? Math.ceil(diffMs / 1000) : 0;
  };

  const [secondsLeft, setSecondsLeft] = useState<number>(() => computeSecondsLeft(until));

  // Key the effect on the timestamp so identity churn (new Date each render
  // with the same value) doesn't restart the interval.
  const untilTime = until ? until.getTime() : 0;

  useEffect(() => {
    if (!untilTime) {
      setSecondsLeft(0);
      return;
    }

    const tick = () => {
      const remaining = computeSecondsLeft(new Date(untilTime));
      setSecondsLeft(remaining);
      return remaining;
    };

    // Set immediately so we don't show stale state for up to a second.
    if (tick() <= 0) return;

    const id = setInterval(() => {
      if (tick() <= 0) {
        clearInterval(id);
      }
    }, 1000);

    return () => clearInterval(id);
  }, [untilTime]);

  return { secondsLeft, isActive: secondsLeft > 0 };
}
